import {
  AppBar,
  Button,
  CircularProgress,
  LinearProgress,
  Toolbar
} from '@material-ui/core';
import { Warning as WarningIcon } from '@material-ui/icons';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';

import AppSnackbar from '../common/AppSnackbar';
import { useRemoveJiraWidget } from '../common/useRemoveJiraWidget';
import VerticalStep from '../common/vertical-stepper/VerticalStep';
import VerticalStepper from '../common/vertical-stepper/VerticalStepper';
import VerticalStepperContainer from '../common/vertical-stepper/VerticalStepperContainer';
import ErrorView, { StyledCenterView } from '../common/views/ErrorView';
import { useFindSignup } from '../login/Signup.hooks';
import { BaseOnboardingProps, Signup } from '../login/Signup.interface';
import OnboardingQmsFeatures from './OnboadingQmsFeatures';
import logo from '../assets/logo';
import OnboardingCompanyInfo from './OnboadingCompanyInfo';
import OnboardingInviteUsers from './OnboadingInviteUsers';
import OnboardingThankYou from './OnboadingThankYou';
import { FetchError } from '../common/api/api';
import { useLogout } from '../user/User.hooks';
import { NumberParam, useQueryParam, withDefault } from 'use-query-params';
import { useHistory } from 'react-router-dom';

interface Step {
  label: string;
  large?: boolean;
  render: React.FC<BaseOnboardingProps>;
}

const steps: Step[] = [
  { label: 'Company Info', render: OnboardingCompanyInfo },
  { label: 'QMS Modules', render: OnboardingQmsFeatures, large: true },
  { label: 'Invite User', render: OnboardingInviteUsers },
  { label: 'Complete Setup', render: OnboardingThankYou }
];

const StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  .progress {
    width: 100%;
    z-index: 2;
    position: absolute;
  }
  .container {
    width: 100%;
    height: 100%;
    padding: 32px;
    overflow: auto;
  }
  .content {
    max-width: 768px;
    margin: 0 auto;
    &.large {
      max-width: 1024px;
    }
  }
  .logoutBtn {
    margin-left: auto;
  }
`;

const OnboardingPage: React.FC = () => {
  const history = useHistory();
  const [signup, setSignup] = useState<Signup>();
  const [error, setError] = useState<FetchError>();
  const [didUpdate, setDidUpdate] = useState(false);
  const [selectedStepIndex] = useQueryParam(
    'step',
    withDefault(NumberParam, 0)
  );
  const contentRef = useRef<HTMLDivElement | null>(null);
  const { doLogout } = useLogout();
  const {
    signup: foundSignup,
    isLoading,
    error: findError,
    get
  } = useFindSignup();
  useRemoveJiraWidget();
  const step = steps[selectedStepIndex];

  const handleClickStep = useCallback(
    index => {
      const stepIndex = index >= steps.length ? index - 1 : index;
      // use history to allow pressing browser back button
      history.push(`?step=${stepIndex}`);
    },
    [history]
  );

  const componentProps: BaseOnboardingProps = {
    signup: signup!,
    onError: setError,
    onUpdate: () => setDidUpdate(true),
    onContinue: () => handleClickStep(selectedStepIndex + 1),
    onChange: updated => {
      setSignup(signup => signup && { ...signup, ...updated });
    }
  };

  useEffect(() => {
    get();
  }, [get]);

  useEffect(() => {
    foundSignup && setSignup(foundSignup);
  }, [foundSignup]);

  useEffect(() => {
    setError(findError);
  }, [findError]);

  // scroll to top on index change
  useLayoutEffect(() => {
    contentRef.current?.scrollTo(0, 0);
  }, [selectedStepIndex]);

  const stepsResults = [
    signup &&
      signup.industries.length > 0 &&
      (!signup.hasExistingQms || !!signup.existingQmsInfo),
    signup && signup.features.length > 0,
    true,
    true
  ];
  return (
    <StyledDiv>
      <AppBar position='static' color='secondary' square>
        {isLoading && <LinearProgress className='progress' />}
        <Toolbar>
          <img src={logo} className='logo' alt='Sierra Labs QMS' width={180} />
          <Button
            className='logoutBtn'
            variant='contained'
            size='small'
            color='primary'
            onClick={() => doLogout()}
          >
            Log out
          </Button>
        </Toolbar>
      </AppBar>
      <VerticalStepperContainer>
        <VerticalStepper>
          {steps.map((step, index) => (
            <VerticalStep
              key={step.label + index}
              label={step.label}
              step={index + 1}
              completed={index < selectedStepIndex}
              selected={index === selectedStepIndex}
              onClick={() => {
                // prevent clicking last step so that user
                // can on only navigate it after clicking submit button
                const isEnabled =
                  index < steps.length - 1 &&
                  stepsResults.slice(0, index).every(value => value);
                isEnabled && handleClickStep(index);
              }}
            />
          ))}
        </VerticalStepper>
        {/* <ArtisticBackground ref={scrollElementRef as any}> */}
        <div className='container' ref={contentRef}>
          <div className={`content ${step.large ? 'large' : ''}`}>
            {!signup && !isLoading && (
              <ErrorView
                title='Sign Up details not found'
                description='Unable to load Sign Up data for your account. Please contact your administrator.'
                icon={<WarningIcon color='disabled' fontSize='inherit' />}
              />
            )}
            {!signup && isLoading && (
              <StyledCenterView>
                <div className='content'>
                  <CircularProgress color='primary' size={64} />
                </div>
              </StyledCenterView>
            )}
            {signup && <step.render {...componentProps} />}
          </div>
        </div>
        {/* </ArtisticBackground> */}
        {/* <AppSnackbar
    severity='success'
    open={Boolean(signedAgreementName)}
    onClose={() => setSignedAgreementName(undefined)}
  >
    {signedAgreementName} signed!
  </AppSnackbar>
  <AppSnackbar severity='error' open={Boolean(billingError)}>
    There was an error retreiving the member billing records.
  </AppSnackbar> */}
      </VerticalStepperContainer>

      {/* NOTE: Do not show update messages */}
      {/* <AppSnackbar
        severity='success'
        open={didUpdate}
        onClose={() => setDidUpdate(false)}
      >
        Sign Up details updated.
      </AppSnackbar> */}
      <AppSnackbar
        severity='error'
        open={!!error}
        onClose={() => setError(undefined)}
      >
        {error?.message}
      </AppSnackbar>
    </StyledDiv>
  );
};

export default OnboardingPage;
